import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useState } from 'react';
import { NotificationProvider } from './contexts/NotificationContext';
import { PublicMarketplaceProvider } from './contexts/PublicMarketplaceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { WhatsAppConnectionProvider } from './contexts/WhatsAppConnectionContext';
import AdminLayout from './components/AdminLayout';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import { ProtectedRoute } from './components/ProtectedRoute';

// Each page is loaded only when its route is visited. Keeping these imports
// explicit lets webpack create stable, independently cached portal chunks.
const lazyNamed = (loader, exportName) => lazy(() =>
  loader().then((module) => ({ default: module[exportName] }))
);

// Landing & authentication
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazyNamed(() => import('./pages/auth/Login'), 'Login');
const NotFound = lazy(() => import('./pages/NotFound'));

// Legacy admin portal
const AdminDashboard = lazyNamed(() => import('./pages/admin/Dashboard'), 'AdminDashboard');
const AdminWhatsApp = lazyNamed(() => import('./pages/admin/WhatsApp'), 'AdminWhatsApp');
const AdminWhatsAppTemplates = lazyNamed(() => import('./pages/admin/WhatsAppTemplates'), 'AdminWhatsAppTemplates');
const AdminChats = lazy(() => import('./pages/admin/Chats'));
const AdminContacts = lazyNamed(() => import('./pages/admin/Contacts'), 'AdminContacts');
const AdminStages = lazyNamed(() => import('./pages/admin/Stages'), 'AdminStages');
const AdminTags = lazyNamed(() => import('./pages/admin/Tags'), 'AdminTags');
const AdminTemplates = lazyNamed(() => import('./pages/admin/Templates'), 'AdminTemplates');
const AdminNumbers = lazyNamed(() => import('./pages/admin/Numbers'), 'AdminNumbers');
const AdminMarketing = lazyNamed(() => import('./pages/admin/Marketing'), 'AdminMarketing');
const AdminFlows = lazyNamed(() => import('./pages/admin/Flows'), 'AdminFlows');
const AdminQgames = lazyNamed(() => import('./pages/admin/Qgames'), 'AdminQgames');
const AdminEducation = lazyNamed(() => import('./pages/admin/Education'), 'AdminEducation');
const AdminBilling = lazyNamed(() => import('./pages/admin/Billing'), 'AdminBilling');
const AdminWallet = lazyNamed(() => import('./pages/admin/Wallet'), 'AdminWallet');
const AdminUsers = lazyNamed(() => import('./pages/admin/Users'), 'AdminUsers');
const AdminSettings = lazyNamed(() => import('./pages/admin/Settings'), 'AdminSettings');

// Super admin portal
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const SuperAdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const SuperAdminClients = lazy(() => import('./pages/admin/AdminClients'));
const SuperAdminPlans = lazy(() => import('./pages/admin/AdminPlans'));
const SuperAdminInvoices = lazy(() => import('./pages/admin/Payments'));
const SuperAdminConnections = lazy(() => import('./pages/admin/AdminConnections'));
const AdminFeatures = lazy(() => import('./pages/admin/AdminFeatures'));
const AdminWebhookPools = lazy(() => import('./pages/admin/AdminWebhookPools'));

// Client portal
const ClientDashboard = lazy(() => import('./pages/client/DashboardV2'));
const ClientWhatsApp = lazyNamed(() => import('./pages/client/WhatsApp'), 'ClientWhatsApp');
const WhatsAppNumbers = lazy(() => import('./pages/client/WhatsAppNumbers'));
const Templates = lazyNamed(() => import('./pages/client/Templates'), 'Templates');
const QuickReplies = lazyNamed(() => import('./pages/client/QuickReplies'), 'QuickReplies');
const Tags = lazyNamed(() => import('./pages/client/Tags'), 'Tags');
const ClientChats = lazyNamed(() => import('./pages/client/Chats'), 'ClientChats');
const ClientContacts = lazyNamed(() => import('./pages/client/Contacts'), 'ClientContacts');
const ClientStages = lazyNamed(() => import('./pages/client/Stages'), 'ClientStages');
const ClientReports = lazyNamed(() => import('./pages/client/Reports'), 'ClientReports');
const ClientBilling = lazy(() => import('./pages/client/Billing'));
const ClientSettings = lazyNamed(() => import('./pages/client/Settings'), 'ClientSettings');
const Marketing = lazyNamed(() => import('./pages/client/Marketing'), 'Marketing');
const Flows = lazy(() => import('./pages/client/Flows'));
const FlowEditor = lazy(() => import('./pages/client/FlowEditor'));
const FlowVisualEditor = lazy(() => import('./pages/client/FlowVisualEditor'));
const ClientQGames = lazy(() => import('./pages/client/QGames'));
const QGamesReports = lazy(() => import('./pages/client/QGamesReports'));
const QGameEditor = lazy(() => import('./pages/client/QGameEditor'));
const QGamesList = lazy(() => import('./pages/client/QGamesList'));
const QGamePredictiveResults = lazy(() => import('./pages/client/QGamePredictiveResults'));
const SendGame = lazy(() => import('./pages/client/SendGame'));
const Users = lazy(() => import('./pages/client/Users'));
const Marketplace = lazy(() => import('./pages/client/qgames/marketplace/Marketplace'));
const MarketplaceConfig = lazy(() => import('./pages/client/qgames/marketplace/MarketplaceConfig'));
const Orders = lazy(() => import('./pages/client/qgames/orders/Orders'));
const TransactionsHistory = lazy(() => import('./pages/client/qgames/reports/TransactionsHistory'));

// Education, surveys and engagement modules
const Education = lazy(() => import('./pages/client/Education'));
const CourseEditor = lazy(() => import('./pages/client/CourseEditor'));
const CourseEnrollments = lazy(() => import('./pages/client/CourseEnrollments'));
const SendCourse = lazy(() => import('./pages/client/SendCourse'));
const Surveys = lazy(() => import('./pages/client/Surveys'));
const SurveyCreate = lazy(() => import('./pages/client/SurveyCreate'));
const SurveyResults = lazy(() => import('./pages/client/SurveyResults'));
const SurveySend = lazy(() => import('./pages/client/SurveySend'));
const Raffles = lazy(() => import('./pages/client/Raffles'));
const PharmacyRanking = lazy(() => import('./pages/client/PharmacyRanking'));
const Events = lazy(() => import('./pages/client/Events'));
const Chance = lazy(() => import('./pages/client/Chance'));
const ChanceEditor = lazy(() => import('./pages/client/ChanceEditor'));
const ChanceResults = lazy(() => import('./pages/client/ChanceResults'));
const ChallengesList = lazy(() => import('./pages/client/ChallengesList'));
const ChallengeEditor = lazy(() => import('./pages/client/ChallengeEditor'));
const PharmacyLayout = lazy(() => import('./pages/client/pharmacy/PharmacyLayout'));
const AIAgents = lazy(() => import('./pages/client/AIAgents'));
const AIAgentEditor = lazy(() => import('./pages/client/AIAgentEditor'));
const AIAgentDetail = lazy(() => import('./pages/client/AIAgentDetail'));
const NoCodeTables = lazy(() => import('./pages/client/NoCodeTables'));
const ThemeSettings = lazy(() => import('./pages/client/ThemeSettings'));
const Tutorial = lazy(() => import('./pages/client/Tutorial'));

// Public marketplace and payments
const MarketplaceApp = lazy(() => import('./pages/public/marketplace/MarketplaceApp'));
const CompleteProfile = lazy(() => import('./pages/public/marketplace/CompleteProfile'));
const PublicOrders = lazy(() => import('./pages/public/marketplace/Orders'));
const BuyPoints = lazy(() => import('./pages/public/BuyPoints'));
const PaymentResult = lazy(() => import('./pages/public/PaymentResult'));
const QGamePayment = lazy(() => import('./pages/public/QGamePayment'));
const StartupLogs = lazy(() => import('./pages/public/StartupLogs'));
const AgentDiagnosticLogs = lazy(() => import('./pages/public/AgentDiagnosticLogs'));

const RouteLoadingFallback = () => (
  <div className="route-loading-fallback" role="status" aria-live="polite">
    <span className="route-loading-spinner" aria-hidden="true" />
    <span>Cargando...</span>
  </div>
);


function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null); // 'admin' or 'client'

  const handleLogin = (role) => {
    setIsAuthenticated(true);
    setUserRole(role);
  };

  return (
    <NotificationProvider>
      <PublicMarketplaceProvider>
        <ThemeProvider>
        <WhatsAppConnectionProvider>
        <div className="App">
          <BrowserRouter>
            <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
          {/* Auth Routes */}
          <Route path="/login" element={<Login onLogin={handleLogin} />} />

          {/* Super Admin Portal Routes (Portal Administrativo) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="dashboard" element={<SuperAdminDashboard />} />
              <Route path="clients" element={<SuperAdminClients />} />
              <Route path="plans" element={<SuperAdminPlans />} />
              <Route path="invoices" element={<SuperAdminInvoices />} />
              <Route path="connections" element={<SuperAdminConnections />} />
              <Route path="features" element={<AdminFeatures />} />
              <Route path="webhook-pools" element={<AdminWebhookPools />} />
            </Route>
          </Route>

          {/* Admin Routes (Old) */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
          <Route path="/admin/whatsapp-templates" element={<AdminWhatsAppTemplates />} />
          <Route path="/admin/chats" element={<AdminChats />} />
          <Route path="/admin/contacts" element={<AdminContacts />} />
          <Route path="/admin/stages" element={<AdminStages />} />
          <Route path="/admin/tags" element={<AdminTags />} />
          <Route path="/admin/templates" element={<AdminTemplates />} />
          <Route path="/admin/numbers" element={<AdminNumbers />} />
          <Route path="/admin/marketing" element={<AdminMarketing />} />
          <Route path="/admin/flows" element={<AdminFlows />} />
          <Route path="/admin/qgames" element={<AdminQgames />} />
          <Route path="/admin/qgames/create" element={<AdminQgames />} />
          <Route path="/admin/qgames/reports" element={<AdminQgames />} />
          <Route path="/admin/education" element={<AdminEducation />} />
          <Route path="/admin/education/create" element={<AdminEducation />} />
          <Route path="/admin/education/reports" element={<AdminEducation />} />
          <Route path="/admin/billing" element={<AdminBilling />} />
          <Route path="/admin/wallet" element={<AdminWallet />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/settings" element={<AdminSettings />} />

          {/* Client Routes */}
          {/* Admin Only Routes */}
          <Route path="/client" element={<ProtectedRoute requiredRole="admin" requiredFeature="dashboard"><ClientDashboard /></ProtectedRoute>} />
          <Route path="/client/contacts" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_contacts"><ClientContacts /></ProtectedRoute>} />
          <Route path="/client/stages" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_stages"><ClientStages /></ProtectedRoute>} />
          <Route path="/client/tags" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_stages"><Tags /></ProtectedRoute>} />
          <Route path="/client/numbers" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_numbers"><WhatsAppNumbers /></ProtectedRoute>} />
          <Route path="/client/campaigns" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_marketing"><Marketing /></ProtectedRoute>} />
          <Route path="/client/marketing" element={<ProtectedRoute requiredRole="admin" requiredFeature="whatsapp_marketing"><Marketing /></ProtectedRoute>} />
          {/* Marketplace - Módulo independiente */}
          <Route path="/client/marketplace" element={<ProtectedRoute requiredRole="admin" requiredFeature="marketplace"><Navigate to="/client/marketplace/products" replace /></ProtectedRoute>} />
          <Route path="/client/marketplace/products" element={<ProtectedRoute requiredRole="admin" requiredFeature="marketplace_products"><Marketplace /></ProtectedRoute>} />
          <Route path="/client/marketplace/config" element={<ProtectedRoute requiredRole="admin" requiredFeature="marketplace_settings"><MarketplaceConfig /></ProtectedRoute>} />
          <Route path="/client/marketplace/orders" element={<ProtectedRoute requiredRole="admin" requiredFeature="marketplace_orders"><Orders /></ProtectedRoute>} />
          {/* QGames */}
          <Route path="/client/qgames/reports" element={<ProtectedRoute requiredRole="admin" requiredFeature="qgames_reports"><QGamesReports /></ProtectedRoute>} />
          <Route path="/client/qgames/reports/transactions" element={<ProtectedRoute requiredRole="admin" requiredFeature="qgames_reports"><TransactionsHistory /></ProtectedRoute>} />
          <Route path="/client/billing" element={<ProtectedRoute requiredRole="admin" requiredFeature="billing"><ClientBilling /></ProtectedRoute>} />
          <Route path="/client/tutorial" element={<ProtectedRoute requiredRole="admin"><Tutorial /></ProtectedRoute>} />
          <Route path="/client/users" element={<ProtectedRoute requiredRole="admin" requiredFeature="users_roles"><Users /></ProtectedRoute>} />
          <Route path="/client/settings" element={<ProtectedRoute requiredRole="admin" requiredFeature="settings"><ClientSettings /></ProtectedRoute>} />
          
          {/* Agent & Admin Routes */}
          <Route path="/client/chats" element={<ProtectedRoute requiredFeature="whatsapp_chat"><ClientChats /></ProtectedRoute>} />
          <Route path="/client/templates" element={<ProtectedRoute requiredFeature="whatsapp_templates"><Templates /></ProtectedRoute>} />
          <Route path="/client/quick-replies" element={<ProtectedRoute requiredFeature="whatsapp_templates"><QuickReplies /></ProtectedRoute>} />
          <Route path="/client/flows" element={<ProtectedRoute requiredFeature="whatsapp_flows"><Flows /></ProtectedRoute>} />
          <Route path="/client/flows/new" element={<ProtectedRoute requiredFeature="whatsapp_flows"><FlowEditor /></ProtectedRoute>} />
          <Route path="/client/flows/:flowId/edit" element={<ProtectedRoute requiredFeature="whatsapp_flows"><FlowEditor /></ProtectedRoute>} />
          <Route path="/client/flows/:flowId/design" element={<ProtectedRoute requiredFeature="whatsapp_flows"><FlowVisualEditor /></ProtectedRoute>} />
          <Route path="/client/flows/:flowId/visual" element={<ProtectedRoute requiredFeature="whatsapp_flows"><FlowVisualEditor /></ProtectedRoute>} />
          <Route path="/client/qgames" element={<ProtectedRoute requiredFeature="qgames_manage"><QGamesList /></ProtectedRoute>} />
          <Route path="/client/qgames/list" element={<ProtectedRoute requiredFeature="qgames_manage"><QGamesList /></ProtectedRoute>} />
          <Route path="/client/qgames/create" element={<ProtectedRoute requiredFeature="qgames_manage"><QGameEditor /></ProtectedRoute>} />
          <Route path="/client/qgames/edit/:id" element={<ProtectedRoute requiredFeature="qgames_manage"><QGameEditor /></ProtectedRoute>} />
          <Route path="/client/qgames/send/:id" element={<ProtectedRoute requiredFeature="qgames_manage"><SendGame /></ProtectedRoute>} />
          <Route path="/client/qgames/predictive-results/:id" element={<ProtectedRoute requiredFeature="qgames_manage"><QGamePredictiveResults /></ProtectedRoute>} />

          {/* Education Module Routes */}
          <Route path="/client/education" element={<ProtectedRoute requiredFeature="education"><Education /></ProtectedRoute>} />
          <Route path="/client/education/create" element={<ProtectedRoute requiredFeature="education"><CourseEditor /></ProtectedRoute>} />
          <Route path="/client/education/edit/:courseId" element={<ProtectedRoute requiredFeature="education"><CourseEditor /></ProtectedRoute>} />
          <Route path="/client/education/enrollments/:courseId" element={<ProtectedRoute requiredFeature="education"><CourseEnrollments /></ProtectedRoute>} />
          <Route path="/client/education/send/:courseId" element={<ProtectedRoute requiredFeature="education"><SendCourse /></ProtectedRoute>} />
          
          {/* Surveys Module Routes */}
          <Route path="/client/surveys" element={<ProtectedRoute><Surveys /></ProtectedRoute>} />
          <Route path="/client/surveys/create" element={<ProtectedRoute><SurveyCreate /></ProtectedRoute>} />
          <Route path="/client/surveys/:surveyId/edit" element={<ProtectedRoute><SurveyCreate /></ProtectedRoute>} />
          <Route path="/client/surveys/:surveyId/results" element={<ProtectedRoute><SurveyResults /></ProtectedRoute>} />
          <Route path="/client/surveys/:surveyId/send" element={<ProtectedRoute><SurveySend /></ProtectedRoute>} />

          {/* Raffles Module Routes */}
          <Route path="/client/raffles" element={<ProtectedRoute requiredRole="admin" requiredFeature="raffles"><Raffles /></ProtectedRoute>} />
          <Route path="/client/pharmacy-ranking" element={<ProtectedRoute requiredRole="admin" requiredFeature="pharmacy_ranking"><PharmacyRanking /></ProtectedRoute>} />

          {/* Events Module Routes */}
          <Route path="/client/events" element={<ProtectedRoute requiredRole="admin"><Events /></ProtectedRoute>} />

          {/* Chance Module Routes */}
          <Route path="/client/chance" element={<ProtectedRoute requiredRole="admin" requiredFeature="chance"><Chance /></ProtectedRoute>} />
          <Route path="/client/chance/create" element={<ProtectedRoute requiredRole="admin" requiredFeature="chance"><ChanceEditor /></ProtectedRoute>} />
          <Route path="/client/chance/edit/:chanceId" element={<ProtectedRoute requiredRole="admin" requiredFeature="chance"><ChanceEditor /></ProtectedRoute>} />
          <Route path="/client/chance/results/:chanceId" element={<ProtectedRoute requiredRole="admin" requiredFeature="chance"><ChanceResults /></ProtectedRoute>} />

          {/* Challenges (Retos) Module Routes */}
          <Route path="/client/challenges" element={<ProtectedRoute requiredRole="admin" requiredFeature="challenges"><ChallengesList /></ProtectedRoute>} />
          <Route path="/client/challenges/create" element={<ProtectedRoute requiredRole="admin" requiredFeature="challenges"><ChallengeEditor /></ProtectedRoute>} />
          <Route path="/client/challenges/edit/:challengeId" element={<ProtectedRoute requiredRole="admin" requiredFeature="challenges"><ChallengeEditor /></ProtectedRoute>} />

          {/* Pharmacy Module Routes */}
          <Route path="/client/pharmacy" element={<ProtectedRoute requiredRole="admin" requiredFeature="farmacias"><PharmacyLayout /></ProtectedRoute>} />

          {/* AI Agents Module Routes */}
          <Route path="/client/ai-agents" element={<ProtectedRoute requiredRole="admin" requiredFeature="ai_agents"><AIAgents /></ProtectedRoute>} />
          <Route path="/client/ai-agents/create" element={<ProtectedRoute requiredRole="admin" requiredFeature="ai_agents"><AIAgentEditor /></ProtectedRoute>} />
          <Route path="/client/ai-agents/edit/:agentId" element={<ProtectedRoute requiredRole="admin" requiredFeature="ai_agents"><AIAgentEditor /></ProtectedRoute>} />
          <Route path="/client/ai-agents/:agentId" element={<ProtectedRoute requiredRole="admin" requiredFeature="ai_agents"><AIAgentDetail /></ProtectedRoute>} />
          <Route path="/client/tables" element={<ProtectedRoute requiredRole="admin" requiredFeature="nocode_tables"><NoCodeTables /></ProtectedRoute>} />

          {/* Theme Settings */}
          <Route path="/client/theme" element={<ProtectedRoute requiredRole="admin"><ThemeSettings /></ProtectedRoute>} />

          {/* Public Marketplace Routes - Con slug de tenant */}
          <Route path="/marketplace/:tenantSlug/login" element={<MarketplaceApp />} />
          <Route path="/marketplace/:tenantSlug/profile" element={<CompleteProfile />} />
          <Route path="/marketplace/:tenantSlug/catalog" element={<MarketplaceApp />} />
          <Route path="/marketplace/:tenantSlug/cart" element={<MarketplaceApp />} />
          <Route path="/marketplace/:tenantSlug/checkout" element={<MarketplaceApp />} />
          <Route path="/marketplace/:tenantSlug/orders" element={<PublicOrders />} />
          <Route path="/marketplace/:slug/buy-points" element={<BuyPoints />} />
          <Route path="/marketplace/:slug/payment-result" element={<PaymentResult />} />

          {/* QGame Payment - Página pública de pago para juegos de paga (Wompi) */}
          <Route path="/public/qgames/pay/:executionId" element={<QGamePayment />} />
          
          {/* Rutas legacy sin slug - redirigir a 404 */}
          <Route path="/marketplace/login" element={<NotFound />} />
          <Route path="/marketplace/profile" element={<NotFound />} />
          <Route path="/marketplace/catalog" element={<NotFound />} />
          <Route path="/marketplace/cart" element={<NotFound />} />
          <Route path="/marketplace/checkout" element={<NotFound />} />
          <Route path="/marketplace/orders" element={<NotFound />} />

          {/* Landing Page */}
          <Route path="/" element={<Landing />} />
          
          {/* Debug: Startup Logs */}
          <Route path="/startup-logs" element={<StartupLogs />} />
          <Route path="/agent-logs" element={<AgentDiagnosticLogs />} />
          
          {/* 404 Not Found - Catch all */}
          <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
      </BrowserRouter>
    </div>
    </WhatsAppConnectionProvider>
    </ThemeProvider>
    </PublicMarketplaceProvider>
    </NotificationProvider>
  );
}

export default App;
